import classnames from 'classnames';
import {A} from 'components/atoms/LinksAndButtons/A';
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import {Icon} from 'components/atoms/Media/Icon';

import {P} from 'components/atoms/Text/P';
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {gtmEventsInteractionMailto, gtmEventsInteractionPhone} from 'features/services/googleTagManager/events';
import { HeadlineLevel, HeadlineSize, Margin, ParagraphSize, TextColor } from 'features/ui/enums';

import './style.scss';
import {ContactType} from "features/template/viewModels/ResidenceStage";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    img?: string;
    name: string;
    title: string;
    phone: string;
    mail: string;
    company?: string;
    isMobile?: boolean;
    sizeM?: boolean;
    // Semantisk rubriknivå för namnet. Utelämnad → nuvarande (sizeM ? H4 : H5).
    // Utseendet styrs fortsatt av sizeM via size, så nivån kan sättas utan att storleken ändras.
    level?: HeadlineLevel;
    contactType?: ContactType;
    hideImage?: boolean;
}

export const ContactCard: React.FunctionComponent<Props> = ({img, name, title, phone, mail, company, isMobile, sizeM, level, contactType, hideImage}) => {
    const showRealEstateText = () => contactType === ContactType.REAL_ESTATE_AGENT || contactType === ContactType.REAL_ESTATE_ASSISTANT ||
                               contactType === ContactType.SALES_MANAGER || contactType === ContactType.SALES_MANAGER2 ||
                               contactType === ContactType.EXTERNAL_REAL_ESTATE_AGENT || contactType === ContactType.EXTERNAL_REAL_ESTATE_AGENT2 ||
                               contactType === ContactType.SETTLEMENT_BROKER || contactType === ContactType.SETTLEMENT_BROKER2;
    const showInteriorText = () => contactType === ContactType.INTERIOR_RESPONSIBLE || contactType === ContactType.INTERIOR_RESPONSIBLE2;

    const wrapperClasses = classnames('contact', {
        'contact--medium':  sizeM,
        'contact--no-img':  !img,
        'contact--grayscale':  showInteriorText(),
    });
    const gridClasses = classnames('contact__grid', {
        'contact__grid--has-img':  !hideImage,
    });

    return (
        <div className={wrapperClasses}>
            <div className={gridClasses}>
                {!hideImage &&
                    <div className="contact__img">
                        {img ?
                            <AsyncImage
                                src={img}
                                quality={ImageSizes.SIZE_300}
                                fullHeight
                                noCrop
                                alt={name.replace('#', '') + ", " + title}
                            /> :
                            process.env.NODE_ENV === 'development' && (new Date().getMonth() === 9 && new Date().getDate() > 15) ?
                                <div className='spooky'></div> : //Easter egg
                                <Icon name='block-user' size={64} />
                        }
                    </div>
                }
                <div className="contact__info">
                    {name &&
                        <Headline
                            level={level ?? (sizeM
                                ? HeadlineLevel.H4
                                : HeadlineLevel.H5)
                            }
                            size={sizeM ? HeadlineSize.S : HeadlineSize.XS}
                            className="notranslate"
                        >
                            {name.replace('#', '')}
                        </Headline>
                    }
                    <P 
                        margin={sizeM
                            ? Margin.S
                            : Margin.XS
                        }
                    >
                        {title}
                        {title && company && <br/>}
                        {company && <span className="notranslate">{company}</span>}
                    </P>
                    <P 
                        margin={sizeM
                            ? Margin.S
                            : undefined
                        }
                        size={ParagraphSize.M}
                        className="contact__info__links"
                    >
                        <A
                            href={"mailto:" + mail}
                            attachedEvent={gtmEventsInteractionMailto(window.location.pathname)} 
                            title={'mail: ' + mail}
                        >
                            {(sizeM && !isMobile) && <Icon name='contact-mail' />}
                            {mail}
                        </A>
                        <br/>
                        <A
                            href={"tel:" + phone}
                            title={"tel: " + phone}
                            attachedEvent={gtmEventsInteractionPhone(window.location.pathname)}
                        >
                            {(sizeM && !isMobile) && <Icon name='contact-phone' />}
                            {phone}
                        </A>
                    </P>
                    {sizeM &&
                        <div className="contact__meta-text">
                            {showRealEstateText() &&
                                <P size={ParagraphSize.S} className="contact__help-text" color={TextColor.MUTED}>
                                    <FormattedMessage id="jm.components.organisms.contactCard.realEstateAgentDesc" />
                                </P>
                            }
                            {showInteriorText() &&
                                <P size={ParagraphSize.S} className="contact__help-text">
                                    <FormattedMessage id="jm.components.organisms.contactCard.interiorAgentDesc" />
                                </P>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    )
};

